import { footerMenuColumns as footerColumns } from '@/constants/mega-menu'
import Link from 'next/link'
import LinkHoverGroup from '@/components/ui/link-hover-group'
import { useEffect, useId, useState } from 'react'
import {
  ArrowLeftRight,
  Building2,
  BookOpen,
  CircleDollarSign,
  Code2,
  Info,
  Mail,
  Search,
  Share2,
  Wrench,
  Facebook,
  Instagram,
  Linkedin,
  MapPin,
  Phone,
  Youtube,
} from 'lucide-react'
import Logo from '../nav/logo'
import { usePageTheme } from '@/components/page-theme-provider'
import { organization } from '@/constants/organization'

const headingIcons = {
  'Design & Development': Code2,
  Migrations: ArrowLeftRight,
  'SEO & AI Search': Search,
  'Paid Media & Conversion': CircleDollarSign,
  'Retention Marketing': Mail,
  'Support & Optimization': Wrench,
  Company: Building2,
  Resources: BookOpen,
  Information: Info,
  'Connect with us': Share2,
}

function FooterHeading({ children }) {
  const Icon = headingIcons[children]
  const gradientId = `footer-icon-${useId()}`
  const pageTheme = usePageTheme()
  const colors = pageTheme.key === 'green' ? pageTheme.stops : ['#0629C8', '#0A65F1', '#049FF2']
  return (
    <h3 className="flex items-center gap-2 text-sm font-bold leading-6 text-black">
      <Icon className="size-4 shrink-0" stroke={`url(#${gradientId})`} aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={colors[0]} />
            <stop offset="50%" stopColor={colors[1]} />
            <stop offset="100%" stopColor={colors[2]} />
          </linearGradient>
        </defs>
      </Icon>
      {children}
    </h3>
  )
}

const FooterLinks = ({ flush = false }) => {
  const headingId = useId()
  const pageTheme = usePageTheme()
  const focusOutline = pageTheme.key === 'green' ? 'focus-visible:outline-shopify2' : 'focus-visible:outline-blue-500'
  const [date, setDate] = useState(null)

  useEffect(() => {
    setDate(new Date().getFullYear())
  }, [])

  const navigation = {
    social: [
      {
        name: 'Facebook',
        href: 'http://www.facebook.com/venturawebdesign',
        icon: <Facebook className='size-5' />,
      },
      {
        name: 'Instagram',
        href: 'https://www.instagram.com/venturawebdesign/',
        icon: <Instagram className='size-5' />,
      },
      {
        name: 'X',
        href: 'https://x.com/venturaweblv',
        icon: (
          <svg viewBox="0 0 24 24" fill="currentColor" className="size-5">
            <title>X (Twitter)</title>
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        ),
      },
      {
        name: 'LinkedIn',
        href: 'https://www.linkedin.com/company/ventura-web-design/',
        icon: <Linkedin className='size-5' />,
      },
      {
        name: 'YouTube',
        href: 'https://www.youtube.com/user/venturawebdesign',
        icon: <Youtube className='size-5' />,
      },
      {
        name: 'TikTok',
        href: 'https://www.tiktok.com/@venturawebdesign',
        icon: (
          <svg viewBox="0 0 24 24" fill="currentColor" className="size-5">
            <title>TikTok</title>
            <path d="M19.321 5.562a5.123 5.123 0 0 1-.443-.258 6.228 6.228 0 0 1-1.138-.85c-.652-.65-1.076-1.48-1.254-2.454h3.835V2h-5.907v14c0 2.485-2.015 4.5-4.5 4.5S5.414 18.485 5.414 16s2.015-4.5 4.5-4.5c.395 0 .78.051 1.144.147v-2.23a6.75 6.75 0 0 0-1.144-.096 6.5 6.5 0 1 0 6.5 6.5V8.876a8.221 8.221 0 0 0 4.907 1.651v-2.23a5.92 5.92 0 0 1-1-2.735z" />
          </svg>
        ),
      },
    ],
  }
  return (
    <footer className={`bg-white text-black ${flush ? '' : 'mt-16 border-t border-neutral-200'}`}>
      <h2 id={headingId} className="sr-only">
        Footer
      </h2>
      <div className="relative z-10 mx-auto w-full max-w-screen-xl px-5 py-16 md:px-10">
        <div className="space-y-16">
        <LinkHoverGroup as="nav" aria-label="Footer navigation" className="grid min-w-0 grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 xl:grid-cols-[repeat(3,minmax(0,1fr))_max-content]">
          {footerColumns.map((column, index) => (
            <div key={index} className="flex min-w-0 flex-col gap-10">
              {column.sections.map((section) => (
                <section key={section.name}>
                  <FooterHeading>
                    {section.name}
                  </FooterHeading>
                  <ul className="ml-6 mt-4 space-y-3">
                    {section.links.map((item) => (
                      <li key={item.href}>
                        <Link
                          prefetch={false}
                          href={item.href}
                          className={`relative flex w-full text-sm leading-6 text-black no-underline before:absolute before:-inset-x-3 before:-inset-y-1 before:content-[''] hover:text-black hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 ${focusOutline}`}
                        >
                          <span>{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          ))}
        </LinkHoverGroup>
          <div className="grid gap-10 md:grid-cols-[repeat(2,minmax(0,1fr))_max-content] lg:gap-16">
            <div className="space-y-6">
              <div className="w-[200px]">
                <Logo variant={pageTheme.key} />
              </div>
              <p className="max-w-md text-sm leading-6 text-neutral-600">
                {organization.description}
              </p>
            </div>

            <div className="text-neutral-600">
              <div className="mb-4"><FooterHeading>Information</FooterHeading></div>
              <div className="flex items-baseline gap-2.5">
                <MapPin className="relative top-2 shrink-0 size-5" />
                <div
                  className="text-sm leading-6"
                  style={{ marginTop: '-2px' }}
                >
                  {organization.address.streetAddress}
                  <br />
                  {organization.address.addressLocality},{' '}
                  {organization.address.addressRegion}{' '}
                  {organization.address.postalCode}
                </div>
              </div>
              <div className="mt-2.5 flex items-baseline gap-2.5">
                <Phone className="relative top-2 shrink-0 size-5" />
                <a
                  href={`tel:${organization.telephone}`}
                  className="text-sm leading-6"
                  style={{ marginTop: '-2px' }}
                >
                  {organization.telephone.replace('+1-', '')}
                </a>
              </div>
            </div>
            <div>
              <div className="mb-4"><FooterHeading>Connect with us</FooterHeading></div>
          <div className="flex flex-wrap gap-6">
            {navigation.social.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={`text-neutral-600 hover:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 ${focusOutline}`}
              >
                <span className="sr-only">{item.name}</span>
                <div>{item.icon}</div>
              </a>
            ))}
          </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#F8F8F8] py-3">
        <div className="mx-auto flex w-full max-w-screen-xl flex-wrap items-center justify-between gap-3 px-5 md:px-10">
          <p className="text-xs leading-5 text-neutral-600">
            &copy; {date} • Ventura Web Design & Marketing. All rights reserved.
          </p>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })}
            className={`text-xs leading-5 text-neutral-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 ${focusOutline}`}
          >
            Back to top ↑
          </button>
        </div>
      </div>
    </footer>
  )
}

export default FooterLinks
